import { useEffect, useState } from "react";
import { db } from "../../firebaseconf";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, Button, CircularProgress, Grid, useMediaQuery,
    Alert, Chip, Card,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Layout from './../../components/layout';
import Header from './../../components/header';
import Preloader from './../../components/preloader';
import Alerts from "../../components/alert";

export default function CheckAssignment() {
    const [assignments, setAssignments] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingLoading, setSavingLoading] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [marksData, setMarksData] = useState({});
    const [remarksData, setRemarksData] = useState({});
    const [institutionId, setInstitutionId] = useState('');

    const isMobile = useMediaQuery("(max-width: 768px)");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const loggedUser = JSON.parse(sessionStorage.getItem("logged_user"));
                if (!loggedUser || !loggedUser.institution_id) return;

                setInstitutionId(loggedUser.institution_id);
                const instId = loggedUser.institution_id;

                // Fetch teacher's assignments
                const assignmentsRef = collection(db, 'institutions', instId, 'assignments');
                const assignmentsQ = query(assignmentsRef, where('teacher_id', '==', loggedUser.id));
                const assignmentsSnap = await getDocs(assignmentsQ);
                const assignmentsList = assignmentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                setAssignments(assignmentsList);

                // Fetch all submissions for teacher's assignments
                if (assignmentsList.length > 0) {
                    const assignmentIds = assignmentsList.map(a => a.id);
                    const submissionsRef = collection(db, 'institutions', instId, 'submissions');

                    // Fetch all submissions and filter client-side (Firestore 'in' max 10)
                    const allSubmissionsSnap = await getDocs(submissionsRef);
                    const allSubmissions = allSubmissionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                    setSubmissions(allSubmissions.filter(s => assignmentIds.includes(s.assignment_id)));
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const getSubmissionsForAssignment = (assignmentId) => {
        return submissions.filter(s => s.assignment_id === assignmentId);
    };

    const handleMarksChange = (submissionId, value) => {
        setMarksData(prev => ({ ...prev, [submissionId]: value }));
    };

    const handleRemarksChange = (submissionId, value) => {
        setRemarksData(prev => ({ ...prev, [submissionId]: value }));
    };

    const handleGradeSubmit = async (submission, totalMarks) => {
        const marks = marksData[submission.id];
        const remarks = remarksData[submission.id];

        if (!marks || !remarks) {
            alert("Please fill in both Marks and Remarks.");
            return;
        }
        if (parseFloat(marks) > totalMarks) {
            alert(`Marks cannot exceed total marks (${totalMarks}).`);
            return;
        }

        setSavingLoading(true);
        try {
            const submissionRef = doc(db, 'institutions', institutionId, 'submissions', submission.id);
            await updateDoc(submissionRef, {
                marks_obtained: parseFloat(marks),
                remarks: remarks,
                checked: true,
                status: 'graded',
                gradedAt: new Date()
            });

            setAlertVisible(true);
            setTimeout(() => setAlertVisible(false), 3000);

            // Update local state
            setSubmissions(prev => prev.map(s =>
                s.id === submission.id
                    ? { ...s, marks_obtained: parseFloat(marks), remarks, checked: true, status: 'graded' }
                    : s
            ));
        } catch (error) {
            console.error("Error grading submission:", error);
            alert(`Error: ${error.message}`);
        } finally {
            setSavingLoading(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        if (dateValue.seconds) return new Date(dateValue.seconds * 1000).toLocaleDateString();
        return new Date(dateValue).toLocaleDateString();
    };

    return (
        <Layout>
            <Header title="Check Assignments" />
            {savingLoading && <Preloader />}
            {alertVisible && <Alerts message="Submission graded successfully!" type="success" color="success" />}

            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Manage Assignments
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : assignments.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                        <AssignmentIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                        <Typography>No assignments found. Create assignments from the Add Assignment page.</Typography>
                    </Card>
                ) : (
                    assignments.map(assignment => {
                        const assignmentSubmissions = getSubmissionsForAssignment(assignment.id);
                        const gradedCount = assignmentSubmissions.filter(s => s.checked).length;

                        return (
                            <Accordion key={assignment.id} sx={{ mb: 2, boxShadow: 2, borderRadius: '8px !important' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                            {assignment.title}
                                        </Typography>
                                        <Chip label={assignment.subject || 'N/A'} size="small" color="primary" variant="outlined" />
                                        <Chip
                                            label={`${assignmentSubmissions.length} Submissions`}
                                            size="small"
                                            color={assignmentSubmissions.length > 0 ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                        {gradedCount > 0 && (
                                            <Chip label={`${gradedCount} Graded`} size="small" color="info" />
                                        )}
                                        <Chip label={`Due: ${formatDate(assignment.dueDate)}`} size="small" color="warning" variant="outlined" />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography><strong>Description:</strong> {assignment.description || 'N/A'}</Typography>
                                            <Typography><strong>Total Marks:</strong> {assignment.total_marks || 'N/A'}</Typography>
                                        </Grid>
                                    </Grid>

                                    {assignmentSubmissions.length === 0 ? (
                                        <Alert severity="info">No submissions yet for this assignment.</Alert>
                                    ) : (
                                        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 1, borderRadius: 2 }}>
                                            <Table size={isMobile ? "small" : "medium"}>
                                                <TableHead sx={{ backgroundColor: '#f0f4ff' }}>
                                                    <TableRow>
                                                        <TableCell><strong>Student</strong></TableCell>
                                                        <TableCell><strong>Submitted</strong></TableCell>
                                                        <TableCell><strong>File</strong></TableCell>
                                                        <TableCell><strong>Status</strong></TableCell>
                                                        <TableCell><strong>Marks</strong></TableCell>
                                                        <TableCell><strong>Remarks</strong></TableCell>
                                                        <TableCell><strong>Action</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {assignmentSubmissions.map(submission => (
                                                        <TableRow key={submission.id}>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {submission.student_name || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {submission.class || ''}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>{formatDate(submission.submittedDate || submission.createdAt)}</TableCell>
                                                            <TableCell>
                                                                {submission.pdfUrl ? (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        href={submission.pdfUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        View
                                                                    </Button>
                                                                ) : 'No file'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={submission.checked ? 'Graded' : 'Pending'}
                                                                    color={submission.checked ? 'success' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {submission.checked ? (
                                                                    <Typography fontWeight={600}>
                                                                        {submission.marks_obtained} / {assignment.total_marks}
                                                                    </Typography>
                                                                ) : (
                                                                    <TextField
                                                                        type="text"
                                                                        size="small"
                                                                        sx={{ width: 80 }}
                                                                        placeholder="Marks"
                                                                        inputProps={{ min: 0, max: assignment.total_marks || 100 }}
                                                                        value={marksData[submission.id] || ""}
                                                                        onChange={(e) => handleMarksChange(submission.id, e.target.value)}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {submission.checked ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {submission.remarks || 'N/A'}
                                                                    </Typography>
                                                                ) : (
                                                                    <TextField
                                                                        type="text"
                                                                        size="small"
                                                                        sx={{ width: 120 }}
                                                                        placeholder="Remarks"
                                                                        value={remarksData[submission.id] || ""}
                                                                        onChange={(e) => handleRemarksChange(submission.id, e.target.value)}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {!submission.checked && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        disabled={savingLoading}
                                                                        onClick={() => handleGradeSubmit(submission, assignment.total_marks)}
                                                                    >
                                                                        {savingLoading ? <CircularProgress size={18} /> : "Grade"}
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                )}
            </Box>
        </Layout>
    );
}
